/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import dayjs from 'dayjs';

export type RecurrenceFrequency = 'none' | 'daily' | 'weekly' | 'monthly';

const FREQ_MAP: Record<Exclude<RecurrenceFrequency, 'none'>, string> = {
  daily: 'DAILY',
  weekly: 'WEEKLY',
  monthly: 'MONTHLY',
};

/**
 * Builds an RFC 5545 RRULE list for the given frequency, or empty when none.
 * @param freq - The recurrence frequency.
 * @returns An array with a single RRULE string, or an empty array.
 */
export const buildRecurrence = (freq: RecurrenceFrequency): string[] => {
  if (freq === 'none') return [];
  return [`RRULE:FREQ=${FREQ_MAP[freq]}`];
};

/**
 * Combines a `YYYY-MM-DD` date and `HH:mm` time into an ISO UTC string.
 * @param date - The calendar date.
 * @param time - The wall-clock time.
 * @returns The combined instant as an ISO 8601 UTC string.
 */
export const toIsoUtc = (date: string, time: string): string =>
  dayjs(`${date}T${time}`).toISOString();
